import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { batchAPI } from '../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import DashboardLayout from '../components/DashboardLayout';
import { Upload, FolderOpen, CheckCircle2, AlertCircle, Cloud, X, ArrowLeft } from 'lucide-react';

const BatchCreate = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [batchName, setBatchName] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  const [validationStatus, setValidationStatus] = useState('');
  const fileInputRef = useRef(null);
  const cancelTokenRef = useRef(null);
  const [validating, setValidating] = useState(false);
  const [validationProgress, setValidationProgress] = useState(0);
  const validationIntervalRef = useRef(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState(null);

  // Redirect inactive vendors
  useEffect(() => {
    if (user?.is_active === false) {
      navigate('/batches');
    }
  }, [user, navigate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (validationIntervalRef.current) {
        clearInterval(validationIntervalRef.current);
      }
    };
  }, []);

  const handleFolderSelect = (e) => {
    const files = Array.from(e.target.files);

    // Clear any previous errors and status
    setError('');
    setValidationStatus('');

    if (files.length === 0) {
      setError('Please select a folder with files');
      return;
    }

    // Extract folder name from the first file's path
    const firstFilePath = files[0].webkitRelativePath;
    const folderName = firstFilePath.split('/')[0];

    // Validate folder structure
    const hasGroupMetadata = files.some(f =>
      f.name.toLowerCase().includes('groupmetadataimage') &&
      (f.name.endsWith('.xlsx') || f.name.endsWith('.csv'))
    );

    const hasArtifactsFolder = files.some(f => {
      const pathParts = f.webkitRelativePath.split('/');
      return pathParts.length > 1 &&
        (pathParts[1].toLowerCase() === 'artifacts' || pathParts[1].toLowerCase() === 'artifact');
    });

    const tiffFiles = files.filter(f => {
      const pathParts = f.webkitRelativePath.split('/');
      const isInArtifacts = pathParts.length > 2 &&
        (pathParts[1].toLowerCase() === 'artifacts' || pathParts[1].toLowerCase() === 'artifact');
      const isTiff = f.name.toLowerCase().endsWith('.tif') || f.name.toLowerCase().endsWith('.tiff');
      return isInArtifacts && isTiff;
    });

    // Reject if ArtifactMetadata file is included (system-generated, should not be uploaded)
    const hasArtifactMetadata = files.some(f =>
      f.name.toLowerCase().includes('artifactmetadata')
    );
    if (hasArtifactMetadata) {
      setError('Your folder contains "ArtifactMetadata" file. This file is auto-generated by the system — please remove it from your folder and try again.');
      setSelectedFolder(null);
      setBatchName('');
      return;
    }

    if (!hasGroupMetadata) {
      setError('Missing GroupMetadataImage file (.xlsx or .csv) in the root folder');
      setSelectedFolder(null);
      setBatchName('');
      return;
    }

    if (!hasArtifactsFolder) {
      setError('Missing "artifacts" or "Artifacts" folder');
      setSelectedFolder(null);
      setBatchName('');
      return;
    }

    if (tiffFiles.length === 0) {
      setError('No .tiff files found in artifacts folder');
      setSelectedFolder(null);
      setBatchName('');
      return;
    }

    // Check sequence order — extract trailing numbers from filenames and detect gaps
    const tiffNames = tiffFiles.map(f => f.name).sort();
    const sequenceNumbers = tiffNames.map(name => {
      const match = name.match(/(\d+)(?=\.[^.]+$)/);
      return match ? parseInt(match[1], 10) : null;
    }).filter(n => n !== null);

    if (sequenceNumbers.length > 0) {
      const minSeq = Math.min(...sequenceNumbers);
      const maxSeq = Math.max(...sequenceNumbers);
      const sequenceSet = new Set(sequenceNumbers);
      const missing = [];
      for (let i = minSeq; i <= maxSeq; i++) {
        if (!sequenceSet.has(i)) {
          missing.push(String(i).padStart(String(maxSeq).length, '0'));
        }
      }
      if (missing.length > 0) {
        setError(`Sequence order is incorrect. Missing sequence number(s): ${missing.join(', ')}`);
        setSelectedFolder(null);
        setBatchName('');
        return;
      }
    }

    setSelectedFolder({
      name: folderName,
      files: files,
      tiffFiles: tiffFiles,
      tiffCount: tiffFiles.length
    });
    setBatchName(folderName);
    setError('');
    setValidationStatus(`Found ${tiffFiles.length} TIFF images in artifacts folder`);
  };

  const pollValidationProgress = async (batchId) => {
    try {
      const response = await batchAPI.getValidationProgress(batchId);
      const progress = response.data;

      setValidationProgress(progress.progress_percentage);
      setValidationStatus(
        `Validating images: ${progress.processed_images}/${progress.total_images} (${progress.progress_percentage.toFixed(1)}%)`
      );

      // If validation is complete
      if (progress.current_status === 'waiting' || progress.current_status === 'errors') {
        if (validationIntervalRef.current) {
          clearInterval(validationIntervalRef.current);
          validationIntervalRef.current = null;
        }
        setValidating(false);
        setValidationStatus('Validation complete!');

        // Show success message
        setTimeout(() => {
          setSuccessData({
            batchId,
            batchName,
            totalImages: progress.total_images,
            passedImages: progress.passed_images,
            failedImages: progress.failed_images
          });
          setShowSuccessModal(true);
        }, 1000);
      }
    } catch (error) {
      console.error('Error polling validation progress:', error);
    }
  };

  const handleUploadBatch = async () => {
    if (!selectedFolder) {
      setError('Please select a folder first');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setValidationProgress(0);
    setError('');
    setValidationStatus('☁️ Requesting secure upload URLs from AWS S3...');

    try {
      // Step 1: Build file paths array (preserve folder structure)
      const filePaths = selectedFolder.files.map(file => {
        const relativePath = file.webkitRelativePath.split('/').slice(1).join('/');
        return relativePath;
      });

      // Step 2: Request presigned URLs from backend
      const presignedResponse = await batchAPI.requestS3Upload({
        batch_name: batchName,
        file_paths: filePaths
      });

      const { batch_id, presigned_urls, concurrent_uploads } = presignedResponse.data;
      setValidationStatus(`☁️ Uploading ${filePaths.length} files directly to AWS S3 cloud storage...`);

      // Step 3: Upload files directly to S3 using presigned URLs (PARALLEL for speed!)
      const totalFiles = selectedFolder.files.length;
      let uploadedFiles = 0;

      // Use concurrent upload limit from backend (configurable via .env)
      const CONCURRENT_UPLOADS = concurrent_uploads || 10;
      console.log(`Using ${CONCURRENT_UPLOADS} concurrent uploads`);

      // Function to upload a single file with retry logic
      const uploadFileWithRetry = async (file, maxRetries = 3) => {
        const relativePath = file.webkitRelativePath.split('/').slice(1).join('/');
        const presignedUrl = presigned_urls[relativePath];

        if (!presignedUrl) {
          console.error(`No presigned URL for: ${relativePath}`);
          throw new Error(`No presigned URL for: ${relativePath}`);
        }

        // Retry logic
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            await batchAPI.uploadToS3(presignedUrl, file);

            uploadedFiles++;
            setUploadProgress(Math.round((uploadedFiles / totalFiles) * 100));
            setValidationStatus(`☁️ Uploaded ${uploadedFiles}/${totalFiles} files to AWS S3 (${Math.round((uploadedFiles / totalFiles) * 100)}%)`);

            return { success: true, file: relativePath };
          } catch (error) {
            console.error(`Upload attempt ${attempt}/${maxRetries} failed for ${relativePath}:`, error);

            if (attempt === maxRetries) {
              return { success: false, file: relativePath, error: error.message };
            }

            // Wait before retry (exponential backoff: 1s, 2s, 4s)
            await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, attempt - 1)));
          }
        }
      };

      // Upload files in parallel batches using Promise.allSettled
      const failedFiles = [];

      for (let i = 0; i < selectedFolder.files.length; i += CONCURRENT_UPLOADS) {
        const batch = selectedFolder.files.slice(i, i + CONCURRENT_UPLOADS);
        const results = await Promise.allSettled(batch.map(file => uploadFileWithRetry(file)));

        // Check for failures
        results.forEach((result, index) => {
          if (result.status === 'rejected' || (result.status === 'fulfilled' && !result.value.success)) {
            const fileName = batch[index].webkitRelativePath.split('/').slice(1).join('/');
            failedFiles.push({
              name: fileName,
              error: result.reason || result.value?.error || 'Unknown error'
            });
          }
        });
      }

      // If any files failed, show error and stop
      if (failedFiles.length > 0) {
        setUploading(false);
        setUploadProgress(0);

        const errorMessage = `Failed to upload ${failedFiles.length} file(s) after 3 retries:\n${failedFiles.map(f => `- ${f.name}: ${f.error}`).join('\n')}`;
        setValidationStatus(`❌ Upload Failed: ${failedFiles.length} file(s) could not be uploaded. Please try again.`);

        alert(errorMessage);
        console.error('Upload failures:', failedFiles);
        return;
      }

      // Step 4: Confirm upload completion to backend
      setValidationStatus('✅ Confirming S3 upload completion with server...');
      await batchAPI.confirmS3Upload(batch_id);

      // Upload complete
      setUploading(false);
      setUploadProgress(100);
      setValidationStatus('✅ S3 Upload Complete! Files are in cloud storage. Waiting for validation scheduler...');

      // Show validation bar
      setValidating(true);

      // Step 5: Start polling for validation progress
      validationIntervalRef.current = setInterval(() => {
        pollValidationProgress(batch_id);
      }, 2000); // Poll every 2 seconds

      // Initial poll
      pollValidationProgress(batch_id);

    } catch (error) {
      console.error('Error uploading batch:', error);
      setError(error.response?.data?.detail || 'Failed to upload batch. Please try again.');
      setValidationStatus('');
      setUploading(false);
      setValidating(false);

      // Clear validation interval if error occurs
      if (validationIntervalRef.current) {
        clearInterval(validationIntervalRef.current);
        validationIntervalRef.current = null;
      }
    }
  };

  const handleCancelUpload = () => {
    if (uploading && cancelTokenRef.current) {
      // Cancel the ongoing upload
      cancelTokenRef.current.cancel('Upload cancelled by user');
      setValidationStatus('Cancelling upload...');
    } else {
      // Clear selection and stay on the same page
      setSelectedFolder(null);
      setBatchName('');
      setError('');
      setValidationStatus('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const styles = `
    .page-header {
      margin-bottom: 32px;
    }

    .page-title {
      font-size: 28px;
      font-weight: 800;
      color: hsl(var(--foreground));
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .page-subtitle {
      font-size: 14px;
      color: hsl(var(--muted-foreground));
    }

    .upload-container {
      max-width: 800px;
      margin: 0 auto;
    }

    .upload-card {
      background: var(--gradient-card);
      border-radius: var(--radius-lg);
      border: 1px solid hsl(var(--border));
      box-shadow: var(--shadow-card);
      padding: 32px;
    }

    .s3-banner {
      background: var(--gradient-primary);
      padding: 16px 20px;
      border-radius: var(--radius-md);
      margin-bottom: 20px;
      display: flex;
      align-items: center;
      gap: 12px;
      color: white;
    }

    .info-box {
      padding: 16px;
      background: hsl(var(--info) / 0.1);
      border: 1px solid hsl(var(--info) / 0.2);
      border-radius: var(--radius-md);
      margin-bottom: 24px;
    }

    .info-title {
      font-size: 13px;
      font-weight: 600;
      color: hsl(var(--info));
      margin: 0 0 8px 0;
    }

    .info-list {
      margin: 0 0 0 20px;
      padding: 0;
      color: hsl(var(--info));
      font-size: 12px;
      line-height: 1.8;
    }

    .error-box {
      padding: 12px 16px;
      background: hsl(var(--destructive) / 0.1);
      border: 1px solid hsl(var(--destructive) / 0.3);
      border-radius: var(--radius-md);
      color: hsl(var(--destructive));
      font-size: 13px;
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .status-box {
      padding: 16px;
      background: hsl(var(--info) / 0.1);
      border: 1px solid hsl(var(--info) / 0.2);
      border-radius: var(--radius-md);
      margin-bottom: 16px;
    }

    .upload-area {
      border: 2px dashed hsl(var(--border));
      border-radius: var(--radius-md);
      padding: 48px 24px;
      text-align: center;
      background: hsl(var(--secondary) / 0.3);
      cursor: pointer;
      transition: all 0.3s;
      margin-bottom: 24px;
    }

    .upload-area:hover {
      border-color: hsl(var(--primary));
      background: hsl(var(--primary) / 0.05);
    }

    .upload-icon {
      font-size: 48px;
      margin-bottom: 16px;
    }

    .upload-text {
      font-size: 16px;
      font-weight: 600;
      color: hsl(var(--foreground));
      margin: 0 0 8px 0;
    }

    .upload-hint {
      font-size: 13px;
      color: hsl(var(--muted-foreground));
      margin: 0;
    }

    .selected-folder {
      padding: 16px;
      background: hsl(var(--success) / 0.1);
      border: 1px solid hsl(var(--success) / 0.3);
      border-radius: var(--radius-md);
      margin-bottom: 24px;
    }

    .folder-name {
      font-size: 15px;
      font-weight: 600;
      color: hsl(var(--success));
      margin: 0 0 8px 0;
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .folder-info {
      font-size: 13px;
      color: hsl(var(--success));
      margin: 4px 0;
    }

    .progress-container {
      margin-bottom: 16px;
    }

    .progress-header {
      margin-bottom: 8px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .progress-label {
      font-size: 14px;
      font-weight: 600;
      color: hsl(var(--info));
    }

    .progress-percent {
      font-size: 13px;
      color: hsl(var(--info));
    }

    .progress-bar {
      width: 100%;
      height: 24px;
      background: hsl(var(--secondary));
      border-radius: 9999px;
      overflow: hidden;
    }

    .progress-fill {
      height: 100%;
      background: var(--gradient-primary);
      transition: width 0.3s ease;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .progress-text {
      font-size: 11px;
      font-weight: 600;
      color: white;
    }

    .button-group {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
    }

    .btn-cancel {
      padding: 10px 20px;
      font-size: 14px;
      font-weight: 500;
      border-radius: var(--radius-md);
      cursor: pointer;
      transition: all 0.2s;
      border: 1px solid hsl(var(--border));
      background: hsl(var(--secondary));
      color: hsl(var(--foreground));
    }

    .btn-cancel:hover {
      background: hsl(var(--secondary) / 0.7);
    }

    .btn-upload {
      padding: 10px 20px;
      font-size: 14px;
      font-weight: 600;
      border-radius: var(--radius-md);
      cursor: pointer;
      transition: all 0.2s;
      border: none;
      background: var(--gradient-primary);
      color: white;
      box-shadow: var(--shadow-glow);
    }

    .btn-upload:hover:not(:disabled) {
      transform: scale(1.02);
    }

    .btn-upload:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }

    .hidden-input {
      display: none;
    }

    /* Success Modal */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.7);
      backdrop-filter: blur(4px);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 9999;
      padding: 20px;
    }

    .success-modal {
      background: var(--gradient-card);
      border-radius: var(--radius-lg);
      border: 1px solid hsl(var(--border));
      box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
      max-width: 500px;
      width: 100%;
      position: relative;
      overflow: hidden;
    }

    .modal-header {
      padding: 24px 24px 20px;
      border-bottom: 1px solid hsl(var(--border));
      position: relative;
    }

    .modal-icon {
      width: 56px;
      height: 56px;
      background: var(--gradient-primary);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin: 0 auto 16px;
      box-shadow: var(--shadow-glow);
      animation: scaleIn 0.5s cubic-bezier(0.22, 1, 0.36, 1);
    }

    @keyframes scaleIn {
      from {
        transform: scale(0);
        opacity: 0;
      }
      to {
        transform: scale(1);
        opacity: 1;
      }
    }

    .modal-title {
      font-size: 20px;
      font-weight: 800;
      color: hsl(var(--foreground));
      text-align: center;
      margin: 0 0 8px 0;
    }

    .modal-subtitle {
      font-size: 14px;
      color: hsl(var(--muted-foreground));
      text-align: center;
      margin: 0;
    }

    .modal-batch-name {
      font-weight: 700;
      color: hsl(var(--primary));
    }

    .modal-close {
      position: absolute;
      top: 20px;
      right: 20px;
      width: 32px;
      height: 32px;
      border-radius: var(--radius-md);
      background: hsl(var(--secondary));
      border: 1px solid hsl(var(--border));
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: all 0.2s;
      color: hsl(var(--foreground));
    }

    .modal-close:hover {
      background: hsl(var(--destructive) / 0.1);
      border-color: hsl(var(--destructive));
      color: hsl(var(--destructive));
    }

    .modal-body {
      padding: 24px;
    }

    .stats-grid {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 12px;
      margin-bottom: 20px;
    }

    .stat-card {
      background: hsl(var(--secondary) / 0.5);
      border: 1px solid hsl(var(--border));
      border-radius: var(--radius-md);
      padding: 16px;
      text-align: center;
    }

    .stat-value {
      font-size: 28px;
      font-weight: 800;
      margin-bottom: 4px;
      display: block;
    }

    .stat-label {
      font-size: 12px;
      color: hsl(var(--muted-foreground));
      text-transform: uppercase;
      font-weight: 600;
      letter-spacing: 0.5px;
    }

    .stat-card.total .stat-value {
      color: hsl(var(--info));
    }

    .stat-card.passed .stat-value {
      color: hsl(var(--success));
    }

    .stat-card.failed .stat-value {
      color: hsl(var(--destructive));
    }

    .modal-footer {
      padding: 0 24px 24px;
    }

    .btn-view-batch {
      width: 100%;
      padding: 12px 20px;
      font-size: 14px;
      font-weight: 600;
      border-radius: var(--radius-md);
      cursor: pointer;
      transition: all 0.2s;
      border: none;
      background: var(--gradient-primary);
      color: white;
      box-shadow: var(--shadow-glow);
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
    }

    .btn-view-batch:hover {
      transform: scale(1.02);
    }
  `;

  return (
    <>
      <style>{styles}</style>

      {/* Success Modal */}
      <AnimatePresence>
        {showSuccessModal && successData && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => {
              setShowSuccessModal(false);
              navigate(`/batch/${successData.batchId}`);
            }}
          >
            <motion.div
              className="success-modal"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", duration: 0.5 }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="modal-header">
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate(`/batch/${successData.batchId}`);
                  }}
                  title="Close"
                >
                  <X size={18} />
                </button>
                <div className="modal-icon">
                  <CheckCircle2 size={32} color="white" />
                </div>
                <h2 className="modal-title">Batch Created Successfully!</h2>
                <p className="modal-subtitle">
                  Batch <span className="modal-batch-name">"{successData.batchName}"</span> has been created and validated
                </p>
              </div>

              {/* Body */}
              <div className="modal-body">
                <div className="stats-grid">
                  <div className="stat-card total">
                    <span className="stat-value">{successData.totalImages}</span>
                    <span className="stat-label">Total Images</span>
                  </div>
                  <div className="stat-card passed">
                    <span className="stat-value">{successData.passedImages}</span>
                    <span className="stat-label">Passed</span>
                  </div>
                  <div className="stat-card failed">
                    <span className="stat-value">{successData.failedImages}</span>
                    <span className="stat-label">Failed</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="modal-footer">
                <button
                  className="btn-view-batch"
                  onClick={() => {
                    setShowSuccessModal(false);
                    navigate(`/batch/${successData.batchId}`);
                  }}
                >
                  <CheckCircle2 size={18} />
                  View Batch Details
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <DashboardLayout user={user} logout={logout}>
        <div className="page-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              onClick={() => navigate(-1)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'hsl(var(--muted-foreground))', display: 'flex', alignItems: 'center', padding: '4px' }}
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="page-title">
                <Upload size={32} />
                Upload New Batch
              </h1>
              <p className="page-subtitle">Upload batch folder with GroupMetadataImage and artifacts to AWS S3</p>
            </div>
          </div>
        </div>

        <div className="upload-container">
          <motion.div
            className="upload-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* S3 Upload Info Banner */}
            <div className="s3-banner">
              <Cloud size={24} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '4px' }}>
                  Direct Upload to AWS S3 Cloud Storage
                </div>
                <div style={{ fontSize: '12px', opacity: 0.9 }}>
                  Your files will be securely uploaded directly to Amazon S3 cloud storage with enterprise-grade encryption
                </div>
              </div>
            </div>

            {/* Expected Structure Info */}
            <div className="info-box">
              <h4 className="info-title">Expected Folder Structure:</h4>
              <ul className="info-list">
                <li>📁 BatchName (folder name becomes batch name)</li>
                <li style={{ marginLeft: '20px' }}>📄 GroupMetadataImage.xlsx or .csv</li>
                <li style={{ marginLeft: '20px' }}>📁 artifacts/ or Artifacts/</li>
                <li style={{ marginLeft: '40px' }}>🖼️ image001.tiff</li>
                <li style={{ marginLeft: '40px' }}>🖼️ image002.tiff</li>
                <li style={{ marginLeft: '40px' }}>🖼️ ...</li>
              </ul>
            </div>

            {/* Error Display */}
            {error && (
              <div className="error-box">
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* Upload and Validation Progress */}
            {(uploading || validating) && (
              <div className="status-box">
                {/* Upload Progress */}
                <div className="progress-container">
                  <div className="progress-header">
                    <span className="progress-label">📤 Upload Progress</span>
                    <span className="progress-percent">{uploadProgress}%</span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${uploadProgress}%` }}>
                      <span className="progress-text">{uploadProgress}%</span>
                    </div>
                  </div>
                </div>

                {/* Validation Progress */}
                <div className="progress-container">
                  <div className="progress-header">
                    <span className="progress-label">⚙️ Validation Progress</span>
                    <span className="progress-percent">
                      {validating ? `${validationProgress.toFixed(1)}%` : 'Waiting...'}
                    </span>
                  </div>
                  <div className="progress-bar">
                    <div className="progress-fill" style={{ width: `${validationProgress}%` }}>
                      <span className="progress-text">{validationProgress.toFixed(1)}%</span>
                    </div>
                  </div>
                  {validationStatus && (
                    <div style={{ marginTop: '6px', fontSize: '12px', color: 'hsl(var(--info))' }}>
                      {validationStatus}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Other Status Messages */}
            {validationStatus && !uploading && !validating && !error && (
              <div className="status-box">
                <div style={{ fontSize: '13px', color: 'hsl(var(--info))' }}>
                  ℹ️ {validationStatus}
                </div>
              </div>
            )}

            {/* Hidden file input */}
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFolderSelect}
              webkitdirectory=""
              directory=""
              multiple
              className="hidden-input"
              disabled={uploading || validating}
            />

            {/* Upload Area */}
            {!selectedFolder && (
              <div
                className="upload-area"
                onClick={() => !uploading && !validating && fileInputRef.current?.click()}
              >
                <div className="upload-icon">
                  <FolderOpen size={48} />
                </div>
                <p className="upload-text">Click to Select Batch Folder for S3 Upload</p>
                <p className="upload-hint">
                  Choose a folder - Files will be uploaded directly to AWS S3 cloud storage
                </p>
              </div>
            )}

            {/* Selected Folder Display */}
            {selectedFolder && (
              <div className="selected-folder">
                <h3 className="folder-name">
                  <CheckCircle2 size={20} />
                  <span>{selectedFolder.name}</span>
                </h3>
                <p className="folder-info">
                  ✓ Found {selectedFolder.tiffCount} TIFF images in artifacts folder
                </p>
                <p className="folder-info">
                  ✓ GroupMetadataImage file detected
                </p>
                <p className="folder-info">
                  ✓ Artifacts folder structure valid
                </p>
                {!uploading && !validating && (
                  <button
                    onClick={() => {
                      setSelectedFolder(null);
                      setBatchName('');
                      setValidationStatus('');
                      fileInputRef.current.value = '';
                    }}
                    className="btn-cancel"
                    style={{ marginTop: '12px', padding: '6px 12px', fontSize: '12px' }}
                  >
                    Change Folder
                  </button>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="button-group">
              <button
                onClick={handleCancelUpload}
                className="btn-cancel"
                disabled={validating}
              >
                {uploading ? 'Cancel Upload' : 'Cancel'}
              </button>
              <button
                onClick={handleUploadBatch}
                className="btn-upload"
                disabled={!selectedFolder || uploading || validating}
              >
                {uploading
                  ? `☁️ Uploading to S3... ${uploadProgress}%`
                  : validating
                    ? `⚙️ Validating... ${validationProgress.toFixed(1)}%`
                    : '☁️ Upload to AWS S3 & Validate'}
              </button>
            </div>
          </motion.div>
        </div>
      </DashboardLayout>
    </>
  );
};

export default BatchCreate;
